import React, { useState, useEffect, useCallback } from 'react';
import clsx from 'clsx';
import { UTM_SOURCE_KEY, PARTNER_ID_KEY, AUTH_NETWORKS, CONFIRM_TOKEN_ACTIONS } from '../../../shared/configs/constants';
import { initDefaultCookieAgreement } from '../utils/agreement';
import { useResponseErrorMessage } from '@base/hooks/i18n';
import { injectedComponent, Text, useText } from '@base/core';
import { useSessionStorage } from '@base/hooks/useSessionStorage';

export default injectedComponent(
  {
    name: 'SignUpDialog',
    services: ['api', 'dialog', 'settings', 'notifier'],
    components: ['AuthDialogBase', 'LoginDialog', 'SessionKeyDialog'],
    styles: ['dialog', 'button', 'self'],
  },
  function SignUpDialog({
                          services: { api, dialog, notifier },
                          components: { AuthDialogBase, LoginDialog, SessionKeyDialog },
                          styles: { dialog: dialogStyles, button: buttonStyles, self: selfStyles },
                          diSettings: { privacyNotice, personalData, termsAndConditions, emailTypoSuggest = true },
                        }) {
    const [login, setLogin] = useState('');
    const [password, setPassword] = useState('');
    const [updating, setUpdating] = useState(false);
    const [authError, setAuthError] = useState(null);
    const [hasIncompleteRegistration, setHasIncompleteRegistration] = useState(false);
    const [validationMap, setValidationMap] = useState({
      auth: false,
      agreement: false,
    });
    const [validity, setValidity] = useState(false);

    const text = useText();
    const getErrorMessage = useResponseErrorMessage();

    const storage = useSessionStorage(login);
    const utm_source = sessionStorage.getItem(UTM_SOURCE_KEY) || '';
    const partnerId = sessionStorage.getItem(PARTNER_ID_KEY) || '';

    const persistState = useCallback((key, value) => {
      if (!storage) return;
      storage.set(key, value);
    }, [storage]);

    const getIncompleteRegistrationData = (email) => {
      if (!email) return null;

      try {
        const storageKey = `continuationNotification_${email}`;
        const item = localStorage.getItem(storageKey);
        return item ? JSON.parse(item) : null;
      } catch (error) {
        console.error('Error reading continuation notification:', error);
        return null;
      }
    };

    const handleLoginChange = (value) => {
      if (updating) return;
      setAuthError(null);
      setLogin(value);
    };

    const handlePasswordChange = (value) => {
      if (updating) return;
      setAuthError(null);
      setPassword(value);
    };

    const handleLoginDialog = () => {
      dialog.open(LoginDialog);
    };

    const processResponseError = (response) => {
      if (response && response.result === 'error') {
        if (response.message && response.message.includes('token expired')) {
          setAuthError(text('dialog.signup.error_message__token_expired'));
        } else {
          const message = getErrorMessage(response);
          setAuthError(message);
        }
      }
    };

    const PolicyLink = ({ link, textID }) => (
      <a
        href={link}
        target="_blank"
        className={clsx(dialogStyles.dialogLink, selfStyles.privacyPolicyLink)}
      >
        <Text>{'dialog.signup.' + textID}</Text>
      </a>
    );

    const handleSignUp = async () => {
      notifier.closeAll();
      if (updating) return;

      if (hasIncompleteRegistration) {
        const sessionData = getIncompleteRegistrationData(login);

        if (sessionData && sessionData.memberId) {
          const dialogParams = btoa(JSON.stringify({
            email: login,
            action: CONFIRM_TOKEN_ACTIONS.SIGNUP,
            memberId: sessionData.memberId,
            isFirstLaunch: false,
            utm_source: sessionData.utm_source || utm_source,
            partnerId: sessionData.partnerId || partnerId
          }));
          dialog.open(SessionKeyDialog, dialogParams);
        } else {
          setAuthError(text('dialog.signup.error_message__unknown_error'));
        }
        return;
      }

      if (!validity) return;

      setUpdating(true);
      try {
        const response = await api.signup({
          payload: {
            contact: { identity: 'email', value: login },
            password,
            ...(utm_source ? { utm_source } : {}),
            ...(partnerId ? { partner: partnerId } : {}),
          },
          responseType: 'full',
        });

        if (response && response.result === 'success') {
          notifier.closeAll();
          initDefaultCookieAgreement();

          if (response.data.sessionKeyTTL) {
            const endTimeSec = Math.floor(Date.now() / 1000) + response.data.sessionKeyTTL;
            persistState('longResendTimer', { endTime: endTimeSec });
          }

          const dialogParams = btoa(JSON.stringify({
            email: login,
            action: CONFIRM_TOKEN_ACTIONS.SIGNUP,
            memberId: response.data.memberId,
            sessionKeyTTL: response.data.sessionKeyTTL,
            isFirstLaunch: true,
            utm_source,
            partnerId
          }));
          dialog.open(SessionKeyDialog, dialogParams);
        } else {
          processResponseError(response);
        }
      } catch (e) {
        if (typeof e.json === 'function') {
          const response = await e.json();
          processResponseError(response);
        } else {
          if (e && e.status === 400) {
            try {
              const isLoggedInResponse = await api.isLoggedIn({});
              if (isLoggedInResponse && isLoggedInResponse.isLogged) {
                return;
              }
            } catch (loginCheckError) {
              console.log('Could not check login status:', loginCheckError);
            }
            setAuthError(text('dialog.signup.error_message__token_expired'));
          } else {
            setAuthError(text('dialog.signup.error_message__unknown_error'));
          }
        }
      } finally {
        setUpdating(false);
      }
    };

    const handleSNLogin = (sn, baseUrl, params = {}) => {
      if (!AUTH_NETWORKS.includes(sn)) return;

      const searchParams = new URLSearchParams({
        ...(utm_source && { utm_source }),
        ...(partnerId && { partner: partnerId }),
        ...params,
      });

      const urlParams = searchParams.toString();
      const authUrl = `${baseUrl}/auth/${sn}${urlParams ? `?${urlParams}` : ''}`;

      typeof onSNLogin === 'function'
        ? onSNLogin(sn, baseUrl, params)
        : document.location.assign(authUrl);
    };

    const validateChild = (validationId, childValidity) => {
      const newValidationMap = {
        ...validationMap,
        [validationId]: childValidity,
      };
      setValidationMap(newValidationMap);
      setValidity(Object.values(newValidationMap).every(value => value));
    };

    const handleAgreementChange = (e) => {
      validateChild('agreement', e.target.checked);
    };

    useEffect(() => {
      if (login) {
        const sessionData = getIncompleteRegistrationData(login);

        if (sessionData) {
          setHasIncompleteRegistration(true);
          setValidationMap(prev => ({...prev, agreement: true}));
        } else {
          setHasIncompleteRegistration(false);
          setValidationMap(prev => ({...prev, agreement: false}));
        }
      } else {
        setHasIncompleteRegistration(false);
        setValidationMap(prev => ({...prev, agreement: false}));
      }
    }, [login]);

    return (
      <AuthDialogBase
        login={login}
        onLoginChange={handleLoginChange}
        emailTypoSuggest={emailTypoSuggest}
        password={password}
        onPasswordChange={handlePasswordChange}
        updating={updating}
        authError={authError}
        validationId="auth"
        validity={validity}
        validate={validateChild}
        passwordDisabled={hasIncompleteRegistration}
        passwordPattern="(?=.*[a-zA-Z])(?=.*\d)[A-Za-z\d\x21-\x2F\x3A-\x40\x5B-\x60\x7B-\x7E]{8,}"
        passwordHint={<Text>dialog.signup.hint__password_requirements</Text>}
        title={<Text>dialog.signup.title</Text>}
        beforeActionButton={
          <div className={clsx(dialogStyles.regularText, selfStyles.agreementText)}>
            <span className={dialogStyles.formCheckbox}>
              <input
                type="checkbox"
                id="privacyCheckboxReg"
                className={dialogStyles.formCheckbox__input}
                checked={validationMap.agreement}
                onChange={handleAgreementChange}
              />
              <label
                className={dialogStyles.formCheckbox__checkbox}
                htmlFor="privacyCheckboxReg"
              ></label>
            </span>
            <span>
              Я согласен с
              <PolicyLink link={termsAndConditions} textID="link__terms_and_conditions" />,
              <PolicyLink link={privacyNotice} textID="link__privacy_policy" /> и даю
              <PolicyLink link={personalData} textID="link__personal_data" />
            </span>
          </div>
        }
        actionButtonText={
          hasIncompleteRegistration ?
            <Text>dialog.signup.continue_registration</Text> :
            <Text>dialog.signup.button__create_account</Text>
        }
        onAction={handleSignUp}
        onSNLogin={handleSNLogin}
        afterActionButton={
          <div className={clsx(dialogStyles.regularText, selfStyles.afterActionText)}>
            <Text>dialog.signup.text__have_an_account_q</Text>
            <span className={dialogStyles.dialogLink} onClick={handleLoginDialog}>
              <Text>dialog.signup.link__log_in</Text>
            </span>
          </div>
        }
      />
    );
  }
);
